/**
 * Where a saved view lives, as a link that opens it — the "My views" panel's
 * way to any view from anywhere in the module.
 *
 * A view belongs to one chart on one point (or, for Capacity and RECs, one
 * region), so its link is that chart's url with two params:
 *
 * - `view` — the view to apply. `useMarketView` reads it on arrival, applies
 *   the view, and drops the param.
 * - `strip` — for a Strips view, which strip it belongs to. The mode is the
 *   one piece of chart state not inside a view (`StripsView` keeps it per
 *   point), so the link has to say it; `StripsView` reads and drops it.
 */

import { pointPath, regionPath } from "./paths";

import type { MarketHub, StripMode } from "@/types/market-intelligence";
import type { MarketViewScope } from "@/types/market-views";

export const VIEW_PARAM = "view";
export const STRIP_PARAM = "strip";

/** What each chart is called where a view is listed away from it. */
export const SCOPE_LABELS: Record<MarketViewScope, string> = {
  forwards_strips_rolling: "Rolling strips",
  forwards_strips_calendar: "Calendar strips",
  forwards_strips_quarterly: "Quarterly strips",
  forwards_curve: "Forward curve",
  index_trends: "Index trends",
  index_hourly: "Hourly",
  index_basis: "Basis",
  region_capacity: "Capacity",
  region_recs: "RECs",
};

/** The strip a Strips scope saves views for. */
const STRIP_MODE_BY_SCOPE: Partial<Record<MarketViewScope, StripMode>> = {
  forwards_strips_rolling: "rolling",
  forwards_strips_calendar: "calendar",
  forwards_strips_quarterly: "quarterly",
};

/** Capacity and RECs save views per region (`hub_code` is its slug), not per point. */
export function isRegionScope(scope: MarketViewScope): boolean {
  return scope === "region_capacity" || scope === "region_recs";
}

/** Whether a param value names a strip, for `StripsView` reading `?strip=`. */
export function parseStripMode(value: string | null): StripMode | null {
  return value === "rolling" || value === "calendar" || value === "quarterly" ? value : null;
}

/**
 * The url that opens a view: its chart, on its point or region, with the view
 * applied. `null` for a point view whose point the topology no longer has —
 * there is nowhere to send the reader.
 */
export function viewHref(
  view: { id: string; scope: MarketViewScope; hub_code: string },
  hub: Pick<MarketHub, "commodity" | "iso" | "code"> | undefined,
): string | null {
  const params = new URLSearchParams();
  const mode = STRIP_MODE_BY_SCOPE[view.scope];
  if (mode) params.set(STRIP_PARAM, mode);
  params.set(VIEW_PARAM, view.id);
  const query = `?${params.toString()}`;

  if (view.scope === "region_capacity") return `${regionPath(view.hub_code, "capacity")}${query}`;
  if (view.scope === "region_recs") return `${regionPath(view.hub_code, "recs")}${query}`;
  if (!hub) return null;

  switch (view.scope) {
    case "forwards_strips_rolling":
    case "forwards_strips_calendar":
    case "forwards_strips_quarterly":
      return `${pointPath(hub, "forwards")}/strips${query}`;
    case "forwards_curve":
      return `${pointPath(hub, "forwards")}/curve${query}`;
    case "index_trends":
      return `${pointPath(hub, "index")}/trends${query}`;
    case "index_hourly":
      return `${pointPath(hub, "index")}/hourly${query}`;
    case "index_basis":
      return `${pointPath(hub, "index")}/basis${query}`;
    default:
      return null;
  }
}
